type PasswordPolicy = {
  minimumLength: number;
  maximumLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
};

type PasswordRequirementId =
  | 'minimumLength'
  | 'maximumLength'
  | 'lowercase'
  | 'uppercase'
  | 'number'
  | 'special';

type PasswordRequirementDefinition = {
  id: PasswordRequirementId;
  isEnabled: (policy: PasswordPolicy) => boolean;
  label: (policy: PasswordPolicy) => string;
  isMet: (password: string, policy: PasswordPolicy) => boolean;
};

export type PasswordRequirementCheck = {
  id: PasswordRequirementId;
  label: string;
  isMet: boolean;
};

const PRODUCTION_PASSWORD_POLICY: PasswordPolicy = {
  minimumLength: 12,
  maximumLength: 72,
  requireLowercase: true,
  requireUppercase: true,
  requireNumber: true,
  requireSpecial: true,
};

// Mirrors supabase/config.toml for the local Supabase stack.
const LOCALHOST_PASSWORD_POLICY: PasswordPolicy = {
  minimumLength: 6,
  maximumLength: 72,
  requireLowercase: false,
  requireUppercase: false,
  requireNumber: false,
  requireSpecial: false,
};

const PASSWORD_REQUIREMENT_DEFINITIONS: PasswordRequirementDefinition[] = [
  {
    id: 'minimumLength',
    isEnabled: () => true,
    label: (policy) => `At least ${policy.minimumLength} characters`,
    isMet: (password, policy) => password.length >= policy.minimumLength,
  },
  {
    id: 'maximumLength',
    isEnabled: () => true,
    label: (policy) => `At most ${policy.maximumLength} characters`,
    isMet: (password, policy) => password.length <= policy.maximumLength,
  },
  {
    id: 'lowercase',
    isEnabled: (policy) => policy.requireLowercase,
    label: () => 'One lowercase letter',
    isMet: (password) => /[a-z]/.test(password),
  },
  {
    id: 'uppercase',
    isEnabled: (policy) => policy.requireUppercase,
    label: () => 'One uppercase letter',
    isMet: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'number',
    isEnabled: (policy) => policy.requireNumber,
    label: () => 'One number',
    isMet: (password) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    isEnabled: (policy) => policy.requireSpecial,
    label: () => 'One special character',
    isMet: (password) => /[^A-Za-z0-9]/.test(password),
  },
];

const LOOPBACK_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  '10.0.2.2',
]);

function hostnameFromUrl(value: string | undefined): string | null {
  if (!value) return null;

  try {
    return new URL(value).hostname;
  } catch {
    return null;
  }
}

function currentHostname(): string | null {
  const maybeLocation = (globalThis as typeof globalThis & {
    location?: { hostname?: unknown };
  }).location;

  return typeof maybeLocation?.hostname === 'string'
    ? maybeLocation.hostname
    : null;
}

function isLoopbackHost(hostname: string | null): boolean {
  return hostname !== null && LOOPBACK_HOSTS.has(hostname);
}

function shouldUseLocalhostPasswordPolicy(): boolean {
  if (isLoopbackHost(currentHostname())) return true;

  return isLoopbackHost(hostnameFromUrl(process.env['EXPO_PUBLIC_SUPABASE_URL']));
}

export function getPasswordPolicy(): PasswordPolicy {
  return shouldUseLocalhostPasswordPolicy()
    ? LOCALHOST_PASSWORD_POLICY
    : PRODUCTION_PASSWORD_POLICY;
}

export function getPasswordRequirements(password: string): PasswordRequirementCheck[] {
  const policy = getPasswordPolicy();

  return PASSWORD_REQUIREMENT_DEFINITIONS
    .filter((requirement) => requirement.isEnabled(policy))
    .map((requirement) => ({
      id: requirement.id,
      label: requirement.label(policy),
      isMet: requirement.isMet(password, policy),
    }));
}

function formatPasswordRequirementLabels(labels: string[]): string {
  const normalizedLabels = labels.map((label) => label.charAt(0).toLowerCase() + label.slice(1));

  if (normalizedLabels.length <= 1) return normalizedLabels[0] ?? '';
  if (normalizedLabels.length === 2) {
    return `${normalizedLabels[0]} and ${normalizedLabels[1]}`;
  }

  return `${normalizedLabels.slice(0, -1).join(', ')}, and ${
    normalizedLabels[normalizedLabels.length - 1]
  }`;
}

export function getPasswordPolicyError(password: string): string | null {
  const missingRequirementLabels = getPasswordRequirements(password)
    .filter((requirement) => !requirement.isMet)
    .map((requirement) => requirement.label);

  if (missingRequirementLabels.length === 0) return null;

  return `Password must meet these requirements: ${
    formatPasswordRequirementLabels(missingRequirementLabels)
  }.`;
}
